import { Button } from '@/components/Button';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { CreateEventCallToAction } from '@/partials/dashboard/Events/ListEvents/CreateEventCallToAction';
import { EventCard } from '@/partials/dashboard/Events/ListEvents/EventCard';
import { JoinEventCallToAction } from '@/partials/dashboard/Events/ListEvents/JoinEventCallToAction';
import { useAuthStore } from '@/stores/useAuthStore';
import { useEventStore } from '@/stores/useEventStore';
import { isPC } from '@/utils/device';
import { handleError } from '@/utils/handleError';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaPlus } from 'react-icons/fa';

// Animations variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

export const ListEvents = () => {
  const { events, loading, fetchEvents, filters, setFilters, joinEvent } =
    useEventStore();
  const { user } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eventCode, setEventCode] = useState('');

  useEffect(() => {
    if (user?.id && filters.current_page === 1) {
      const perPage = isPC() ? 20 : 10;

      if (filters.per_page !== perPage) {
        setFilters({ per_page: perPage });
      }

      fetchEvents({
        current_page: filters.current_page || 1,
        per_page: perPage,
        sort_field: filters.sort_field || 'date',
        sort_order: filters.sort_order || 'desc',
        user_id: user.id,
      });
    }
  }, [
    fetchEvents,
    filters.search,
    filters.current_page,
    filters.per_page,
    filters.sort_field,
    filters.sort_order,
    user?.id,
    setFilters,
  ]);

  const fetchMoreData = async () => {
    if (
      loading ||
      (filters.current_page || 1) >= (filters.last_page || 1) ||
      !user?.id
    ) {
      return;
    }

    const nextPage = (filters.current_page || 1) + 1;
    await fetchEvents({
      ...filters,
      current_page: nextPage,
      user_id: user.id,
    });
  };

  const handleJoinEvent = async () => {
    try {
      const { message } = await joinEvent(eventCode);
      toast.success(message);
      setIsModalOpen(false);
      setEventCode('');
      fetchEvents({
        search: filters.search || '',
        current_page: 1,
        per_page: filters.per_page || 10,
        sort_field: filters.sort_field || 'date',
        sort_order: filters.sort_order || 'desc',
        user_id: user?.id,
      });
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div id="scrollableDiv" className="h-full space-y-6 overflow-y-auto">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="space-y-6 px-4 pt-4"
      >
        <div className="flex flex-col items-center justify-between lg:mb-6 lg:flex-row">
          <div className="mb-4 flex w-full flex-row items-center gap-2 whitespace-nowrap md:w-fit lg:mb-0">
            <h1 className="text-lg font-semibold text-secondary">
              {t('listEvents:list.title')}
            </h1>
            <Text className="text-sm font-light text-gray-light">
              ({filters.total})
            </Text>
          </div>
          <Button
            onClick={() => setIsModalOpen(true)}
            className="flex w-full items-center gap-2 md:w-fit"
          >
            <FaPlus />
            {t('listEvents:join.button')}
          </Button>
        </div>
      </motion.div>

      {events.length === 0 ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="flex flex-col items-center justify-center px-4 pb-4"
        >
          <JoinEventCallToAction setIsModalOpen={setIsModalOpen} />
        </motion.div>
      ) : (
        <InfiniteScrollList
          dataLength={events.length}
          next={fetchMoreData}
          hasMore={(filters.current_page || 1) < (filters.last_page || 1)}
          total={events.length}
          scrollableTarget="scrollableDiv"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="px-4"
          >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              <AnimatePresence>
                {events.map((event, index) => (
                  <motion.div key={event.id || index} variants={itemVariants}>
                    <EventCard event={event} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </InfiniteScrollList>
      )}

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="px-4 pb-4"
      >
        <CreateEventCallToAction />
      </motion.div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEventCode('');
        }}
        title={t('listEvents:join.button')}
      >
        <div className="space-y-4">
          <TextInput
            type="text"
            label={t('listEvents:join.code')}
            value={eventCode}
            onChange={setEventCode}
            placeholder={t('listEvents:join.code_placeholder')}
            useFormik={false}
          />
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setIsModalOpen(false);
                setEventCode('');
              }}
              className="bg-danger"
            >
              {t('listEvents:join.cancel')}
            </Button>
            <Button onClick={handleJoinEvent} disabled={!eventCode}>
              {t('listEvents:join.join')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
